/**
 * Editorial workflow (roadmap §5.2): Draft → In Review → Approved → Scheduled → Published,
 * plus Archived. The transition table is the single source of truth and is enforced
 * everywhere (form buttons, repository writes, server).
 */

export const STATUSES = /** @type {const} */ ([
  'draft',
  'in_review',
  'approved',
  'scheduled',
  'published',
  'archived',
]);

export const STATUS_LABEL = {
  draft: 'Draft',
  in_review: 'In review',
  approved: 'Approved',
  scheduled: 'Scheduled',
  published: 'Published',
  archived: 'Archived',
};

const TRANSITIONS = {
  draft: ['in_review', 'archived'],
  in_review: ['draft', 'approved', 'archived'],
  approved: ['scheduled', 'published', 'draft', 'archived'],
  scheduled: ['published', 'draft', 'archived'],
  published: ['archived', 'draft'],
  archived: ['draft'],
};

/** Roles allowed to perform a transition. */
const TRANSITION_ROLES = {
  'in_review': ['admin', 'editor', 'writer'],
  'approved': ['admin', 'editor'],
  'scheduled': ['admin', 'editor'],
  'published': ['admin', 'editor'],
  'archived': ['admin', 'editor'],
  'draft': ['admin', 'editor', 'writer'],
};

const RANK = { admin: 3, editor: 2, writer: 1 };

export function isStatus(v) {
  return STATUSES.includes(v);
}

export function canTransition(from, to) {
  if (from === to) return true;
  return (TRANSITIONS[from] || []).includes(to);
}

export function nextStatuses(from) {
  return TRANSITIONS[from] || [];
}

/** Can `role` move an article from `from` to `to`? (authorisation, not just legality) */
export function canRoleTransition(role, from, to) {
  if (from === to) return true;
  if (!canTransition(from, to)) return false;
  const allowed = TRANSITION_ROLES[to];
  return Array.isArray(allowed) && allowed.includes(role);
}

export function roleRank(role) {
  return RANK[role] || 0;
}

/**
 * Is an article publicly readable right now?
 * Published is public; Scheduled becomes public automatically once due —
 * so a scheduled article can never be "lost" behind a stale status.
 */
export function isPubliclyVisible(article, now = Date.now()) {
  if (!article) return false;
  if (article.status === 'published') return true;
  if (article.status === 'scheduled' && article.scheduledFor) {
    return new Date(article.scheduledFor).getTime() <= now;
  }
  return false;
}
